import { IntlShape } from 'react-intl';
import {
  DialogueLine,
  GoalReachedResponse,
  HintAnimationStatus,
  MessageError,
  MicStatus,
  MissionWithPrompt,
} from 'src/api/membot/types';

export enum WebViewClient {
  ANDROID = 'android',
  IOS = 'ios',
  WEB = 'web',
}

export type AudioStatus = 'inactive' | 'loading' | 'playing' | 'stopped';

export type LanguagesInfo = {
  targetLanguageLocale: string;
  targetLanguageName: string;
  targetLanguageId?: string;
  sourceLanguageLocale: string;
  sourceLanguageName: string;
  sourceLanguageId?: string;
};

export type Audio = {
  audioCurrent: HTMLAudioElement;
  audioStatus: AudioStatus;
};

export interface CommunicateState {
  goalReached: GoalReachedResponse | null;
  arePageDataLoaded: boolean;
  conversationStarted: boolean;
  conversationCompleted: boolean;
  disabled: boolean;
  missionWithPrompt: MissionWithPrompt | null;
  initialMessagesNumber: number;
  message: string;
  messageError?: MessageError;
  dialogList: DialogueLine[];
  micEnabled: boolean;
  micStatus: MicStatus;
  showTranslateHint: boolean;
  showResponseHints: boolean;
  loadingHints: boolean;
  hintLines: DialogueLine[];
  hintAnimationStatus: HintAnimationStatus;
  negativeBotResponseSource: string | null;
  negativeBotResponseTarget: string | null;
  languagesInfo: LanguagesInfo;
  languagePairId: number;
  locationSlug: string;
  errorNotification: string | null;
  fatalError: Error | null;
  client: WebViewClient;
  audios: Audio[];
  sessionId: string;
}

export enum CommunicateEventType {
  PageDataLoaded = 'PageDataLoaded',
  UpdateDialogList = 'UpdateDialogList',
  SetGoalReached = 'SetGoalReached',
  SetDisabled = 'SetDisabled',
  SetMission = 'SetMission',
  SetInitialMessagesNumber = 'SetInitialMessagesNumber',
  SetMessage = 'SetMessage',
  ToggleTranslateHint = 'ToggleTranslateHint',
  SetNegativeBotResponseSource = 'SetNegativeBotResponseSource',
  SetNegativeBotResponseTarget = 'SetNegativeBotResponseTarget',
  UndoLastMessage = 'UndoLastMessage',
  SetShowResponseHints = 'SetShowResponseHints',
  SetHintAnimationStatus = 'SetHintAnimationStatus',
  SetConversationStarted = 'SetConversationStarted',
  SetConversationCompleted = 'SetConversationCompleted',
  SetMicEnabled = 'SetMicEnabled',
  SetMicStatus = 'SetMicStatus',
  SetLoadingHints = 'SetLoadingHints',
  SetHintLines = 'SetHintLines',
  SetErrorNotification = 'SetErrorNotification',
  SetFatalError = 'SetFatalError',
  SetLanguagesInfo = 'SetLanguagesInfo',
  UpdateAudios = 'UpdateAudios',
}

export interface PageDataLoaded {
  type: CommunicateEventType.PageDataLoaded;
}

export interface UpdateDialogList {
  type: CommunicateEventType.UpdateDialogList;
  payload: CommunicateState['dialogList'];
}

export interface SetGoalReached {
  type: CommunicateEventType.SetGoalReached;
  payload: CommunicateState['goalReached'];
}

export interface SetDisabled {
  type: CommunicateEventType.SetDisabled;
  payload: CommunicateState['disabled'];
}

export interface SetMission {
  type: CommunicateEventType.SetMission;
  payload: MissionWithPrompt;
}

export interface SetMessage {
  type: CommunicateEventType.SetMessage;
  payload:
    | CommunicateState['message']
    | ((message: CommunicateState['message']) => CommunicateState['message']);
  intl?: IntlShape;
}

export interface SetInitialMessagesNumber {
  type: CommunicateEventType.SetInitialMessagesNumber;
  payload: CommunicateState['initialMessagesNumber'];
}

export interface ToggleHint {
  type: CommunicateEventType.ToggleTranslateHint;
}

export interface SetNegativeBotResponseSource {
  type: CommunicateEventType.SetNegativeBotResponseSource;
  payload: CommunicateState['negativeBotResponseSource'];
}

export interface SetNegativeBotResponseTarget {
  type: CommunicateEventType.SetNegativeBotResponseTarget;
  payload: CommunicateState['negativeBotResponseTarget'];
}

export interface UndoLastMessage {
  type: CommunicateEventType.UndoLastMessage;
}

export interface SetShowResponseHints {
  type: CommunicateEventType.SetShowResponseHints;
  payload: CommunicateState['showResponseHints'];
}

export interface SetConversationStarted {
  type: CommunicateEventType.SetConversationStarted;
  payload: CommunicateState['conversationStarted'];
}

export interface SetConversationCompleted {
  type: CommunicateEventType.SetConversationCompleted;
  payload: CommunicateState['conversationCompleted'];
}

export interface SetMicEnabled {
  type: CommunicateEventType.SetMicEnabled;
  payload: CommunicateState['micEnabled'];
}

export interface SetMicStatus {
  type: CommunicateEventType.SetMicStatus;
  payload: CommunicateState['micStatus'];
}

export interface SetLoadingHints {
  type: CommunicateEventType.SetLoadingHints;
  payload: CommunicateState['loadingHints'];
}

export interface SetHintLines {
  type: CommunicateEventType.SetHintLines;
  payload: CommunicateState['hintLines'];
}

export interface SetHintAnimationStatus {
  type: CommunicateEventType.SetHintAnimationStatus;
  payload: CommunicateState['hintAnimationStatus'];
}

export interface SetErrorNotification {
  type: CommunicateEventType.SetErrorNotification;
  payload: CommunicateState['errorNotification'];
}

export interface SetFatalError {
  type: CommunicateEventType.SetFatalError;
  payload: CommunicateState['fatalError'];
}

export interface SetLanguagesInfo {
  type: CommunicateEventType.SetLanguagesInfo;
  payload: CommunicateState['languagesInfo'];
}

export interface UpdateAudios {
  type: CommunicateEventType.UpdateAudios;
  payload: Audio;
}

export type CommunicateEvent =
  | PageDataLoaded
  | UpdateDialogList
  | SetGoalReached
  | SetDisabled
  | SetMission
  | SetInitialMessagesNumber
  | SetMessage
  | ToggleHint
  | SetNegativeBotResponseSource
  | SetNegativeBotResponseTarget
  | UndoLastMessage
  | SetShowResponseHints
  | SetConversationStarted
  | SetConversationCompleted
  | SetMicEnabled
  | SetMicStatus
  | SetLoadingHints
  | SetHintLines
  | SetHintAnimationStatus
  | SetErrorNotification
  | SetFatalError
  | SetLanguagesInfo
  | UpdateAudios;
